/**
 * NOS-2: Website Builder v2 shell component.
 * Dark canvas layout with top bar, content switcher, viewport toggle, and canvas area.
 * All visual values from docs/specs/BUILDER_DESIGN_SPEC.md.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { BlockPicker } from "@/components/website-builder/BlockPicker"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { useAuthStore } from "@/stores/authStore"
import { useOrgStore } from "@/stores/orgStore"
import {
  useCustomizationStore,
  type PageSummary,
} from "@/stores/customizationStore"
import { useNavigation, useNavigationMutations } from "@/hooks/useNavigation"
import { useUndoRedoShortcuts } from "@/hooks/useUndoRedoShortcuts"
import { useCreatePage, useDeletePage } from "@/hooks/usePages"
import { useUpdateBlogPost, useDeleteBlogPost, getStatus } from "@/hooks/useBlogPosts"
import { useUpdateCampaign } from "@/hooks/useCampaigns"
import debounce from "lodash.debounce"
import { InlinePreview } from "@/components/website-builder/InlinePreview"
import { OutlineView } from "@/components/website-builder/OutlineView"
import { SectionContextPanel } from "@/components/website-builder/SectionContextPanel"
import { BrandPanel } from "@/components/website-builder/BrandPanel"
import { NavContextPanel } from "@/components/website-builder/NavContextPanel"
import { AnnouncementPanel } from "@/components/website-builder/AnnouncementPanel"
import { PageSettingsPanel } from "@/components/website-builder/PageSettingsPanel"
import { PublishMenu } from "@/components/website-builder/PublishMenu"
import { BUILDER_FONT, PANEL_CHROME } from "@/components/website-builder/ui/FloatingPanel"
import { AICommandBar } from "@/components/website-builder/AICommandBar"
import { GenerationStatusBar } from "@/components/website-builder/GenerationStatusBar"
import { ImportReviewPanel } from "@/components/website-builder/panels/ImportReviewPanel"
import { ImportCompareView } from "@/components/website-builder/ImportCompareView"
import { ContentSwitcherPanel } from "@/components/website-builder/ContentSwitcherPanel"
import { BlogPostCanvas, type BlogLiveData } from "@/components/website-builder/BlogPostCanvas"
import { CampaignCanvas, type CampaignLiveData } from "@/components/website-builder/CampaignCanvas"
import { blocksToContentSections } from "@/components/website-builder/BlogsTab"
import { getDefaultBlockContent } from "@/components/website-builder/blocks/registry"
import type { BlockType } from "@/components/website-builder/blocks/types"
import { useImportReviewSections } from "@/hooks/useImportReviewSections"
import {
  useLatestImportJob,
  imageFailuresFromJob,
  skippedPagesFromJob,
  contentSummaryFromJob,
  overflowSummaryFromJob,
  remainingSummaryFromJob,
  importReportFromJob,
  brandProposalFromJob,
  importDecisionsFromJob,
  importedPostSlugsFromJob,
  importScreenshotsFromJob,
  isImportJobAwaitingReview,
  continueImportJob,
  stopImportJob,
  protectedPagesFromJob,
  protectedRecoveryFromJob,
} from "@/hooks/useImportJob"
import { useIsPlatformAdmin } from "@/hooks/useIsPlatformAdmin"
import type { Organization, PageSection } from "@/templates/types"

const GOOGLE_FONTS_URL =
  "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&family=Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;0,8..60,700&family=Outfit:wght@300;400;500;600;700&family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;0,9..144,700&family=Libre+Franklin:wght@300;400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,600;0,700&display=swap"

type ViewMode = "desktop" | "tablet" | "mobile"

/**
 * NOS-270: shared shape of every live block edit (blog + campaign): apply the
 * mutation, keep order_index sequential, debounce-save, hand back the state.
 * `markMutated` runs before the state update (blog uses it to flag
 * panel-originated changes). Content updates deliberately do NOT reindex —
 * matching the original per-entity handlers.
 */
function makeLiveBlockHandlers<T extends { blocks: PageSection[] }>(
  setLive: React.Dispatch<React.SetStateAction<T | null>>,
  save: (prev: T, blocks: PageSection[]) => void,
  markMutated?: () => void
) {
  const apply = (mutate: (blocks: PageSection[]) => PageSection[], reindex: boolean) => {
    markMutated?.()
    setLive((prev) => {
      if (!prev) return prev
      let newBlocks = mutate(prev.blocks)
      if (reindex) newBlocks = newBlocks.map((b, i) => ({ ...b, order_index: i }))
      save(prev, newBlocks)
      return { ...prev, blocks: newBlocks }
    })
  }
  return {
    update: (sectionId: string, content: Record<string, unknown>) =>
      apply((blocks) => blocks.map((b) => (b.id === sectionId ? { ...b, content } : b)), false),
    remove: (sectionId: string) =>
      apply((blocks) => blocks.filter((b) => b.id !== sectionId), true),
    reorder: (reorderedBlocks: PageSection[]) => apply(() => reorderedBlocks, true),
    append: (block: PageSection) => apply((blocks) => [...blocks, block], true),
    insertAt: (block: PageSection, afterIndex: number) =>
      apply(
        (blocks) => [...blocks.slice(0, afterIndex + 1), block, ...blocks.slice(afterIndex + 1)],
        true
      ),
  }
}

export function WebsiteBuilderPage() {
  const navigate = useNavigate()
  const {
    currentOrg,
    userOrgs,
    isLoading: orgLoading,
    fetchUserOrgs,
    switchOrg,
  } = useOrgStore()
  const {
    load,
    pages,
    sections,
    pageId,
    pageSlug,
    pageTitle,
    isPublished,
    publishWebsite,
    unpublishPage,
    setCurrentPage,
    addPage,
    org,
    customizations,
    loadError,
    addSection,
    reorderSections,
    updateNavCtaText,
    updateAnnouncement,
    removePage,
    removeSection,
    updateSection,
    undoStack,
    redoStack,
    undo,
    redo,
  } = useCustomizationStore()

  const [viewMode, setViewMode] = useState<ViewMode>("desktop")
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null)
  const [selectedSectionIds, setSelectedSectionIds] = useState<string[]>([])
  const [outlineMode, setOutlineMode] = useState(false)
  const [showBrandPanel, setShowBrandPanel] = useState(false)
  const [showAIBar, setShowAIBar] = useState(false)
  // /dashboard/builder?tab=blog opens straight to the blog content panel (NOS-256)
  const [initialContentTab] = useState<"pages" | "blog">(() =>
    new URLSearchParams(window.location.search).get("tab") === "blog" ? "blog" : "pages"
  )
  const [showContentPanel, setShowContentPanel] = useState(initialContentTab === "blog")
  const [activeBlogPostId, setActiveBlogPostId] = useState<string | null>(null)
  const [blogLiveData, setBlogLiveData] = useState<BlogLiveData | null>(null)
  const [activeCampaignId, setActiveCampaignId] = useState<string | null>(null)
  const [campaignLiveData, setCampaignLiveData] = useState<CampaignLiveData | null>(null)
  const [showImportReviewPanel, setShowImportReviewPanel] = useState(true)
  const [showViewportPicker, setShowViewportPicker] = useState(false)
  const viewportPickerRef = useRef<HTMLDivElement>(null)
  const [blogBlockPickerOpen, setBlogBlockPickerOpen] = useState(false)
  const [blogPickerAfterIndex, setBlogPickerAfterIndex] = useState<number | null>(null)
  const [showPageSettings, setShowPageSettings] = useState(false)

  const orgId = currentOrg?.id ?? null
  const { sections: importSections, isLoading: importSectionsLoading, refresh: refreshImportSections } =
    useImportReviewSections(orgId)
  // NOS-178/NOS-179: latest import job — resumes live progress, then feeds the
  // rehost summary (failed image downloads) into the review panel once done.
  const {
    job: activeImportJob,
    isActive: importJobActive,
    refresh: refreshImportJob,
  } = useLatestImportJob(orgId)
  const importImageFailures = imageFailuresFromJob(activeImportJob)
  // NOS-198: pages the mapper deliberately skipped (e.g. the blog index route)
  const importSkippedPages = skippedPagesFromJob(activeImportJob)
  // NOS-186/187: pages-vs-posts rollup + per-type overflow for the review panel
  const importContentSummary = contentSummaryFromJob(activeImportJob)
  const importOverflowSummary = overflowSummaryFromJob(activeImportJob)
  // NOS-192: preview gate — job paused at review awaiting approval
  const importAwaitingReview = isImportJobAwaitingReview(activeImportJob)
  const importRemainingSummary = remainingSummaryFromJob(activeImportJob)
  // NOS-190: persisted import report (skipped/failed pages, discovery sources)
  const importReport = importReportFromJob(activeImportJob)
  // NOS-180: proposed brand tokens from the vision pass (review card)
  const brandProposal = brandProposalFromJob(activeImportJob)
  // NOS-214: guided-review decisions + the post slugs the choices target
  const importDecisions = importDecisionsFromJob(activeImportJob)
  const importedPostSlugs = importedPostSlugsFromJob(activeImportJob)
  // NOS-183: slug → source screenshot for the side-by-side compare view
  const importScreenshots = importScreenshotsFromJob(activeImportJob)
  // NOS-253: pages imported as protected draft stubs + live recovery progress
  const importProtectedPages = protectedPagesFromJob(activeImportJob)
  const importProtectedRecovery = protectedRecoveryFromJob(activeImportJob)
  // NOS-253: ?recover=protected deep link (status card / lifecycle emails) —
  // make sure the review panel (which hosts the recovery card) is open.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get("recover") === "protected") setShowImportReviewPanel(true)
  }, [])
  const [compareSlug, setCompareSlug] = useState<string | null>(null)
  const isPlatformAdmin = useIsPlatformAdmin()
  const handleContinueImport = useCallback(async () => {
    if (!activeImportJob) return
    await continueImportJob(activeImportJob.id)
    refreshImportJob()
  }, [activeImportJob, refreshImportJob])
  const handleStopImport = useCallback(async () => {
    if (!activeImportJob) return
    await stopImportJob(activeImportJob.id)
    refreshImportJob()
  }, [activeImportJob, refreshImportJob])
  const importJobStatusRef = useRef<string | null>(null)
  useEffect(() => {
    const status = activeImportJob?.status ?? null
    // When a live job completes, pull in the freshly imported sections
    if (importJobStatusRef.current === "mapping" && status && status !== "mapping") {
      refreshImportSections()
    }
    importJobStatusRef.current = status
  }, [activeImportJob?.status, refreshImportSections])
  const primaryColor = customizations?.primary_color ?? "#3b82f6"
  const { user } = useAuthStore()
  const { items: navItems, refresh: refreshNav } = useNavigation(orgId)
  const { addNavItem } = useNavigationMutations(orgId)
  const { createPage, loading: pageCreating } = useCreatePage(orgId)
  const { deletePage } = useDeletePage(orgId)
  const { updateBlogPost } = useUpdateBlogPost(orgId)
  const { deleteBlogPost } = useDeleteBlogPost(orgId)
  const { updateContentSections: updateCampaignContentSections } = useUpdateCampaign(orgId)

  // Track whether SectionContextPanel (or add-block) has managed blog blocks,
  // so BlogPostEditor's stale local `blocks` state can't overwrite canvas updates.
  const blogBlocksFromPanelRef = useRef(false)

  const debouncedBlogBlockSave = useMemo(
    () =>
      debounce(async (postId: string, blocks: PageSection[]) => {
        await updateBlogPost(postId, { content_sections: blocksToContentSections(blocks) })
      }, 800),
    [updateBlogPost]
  )

  const debouncedCampaignBlockSave = useMemo(
    () =>
      debounce(async (campaignId: string, blocks: PageSection[]) => {
        await updateCampaignContentSections(campaignId, blocksToContentSections(blocks))
      }, 800),
    [updateCampaignContentSections]
  )

  // Reset panel ref when switching posts so BlogPostEditor's init blocks are used on first load
  useEffect(() => {
    blogBlocksFromPanelRef.current = false
  }, [activeBlogPostId])

  // Called by ContentSwitcherPanel instead of setBlogLiveData directly.
  // After the panel or add-block has taken ownership of blocks, preserve them
  // so BlogPostEditor's stale local state can't overwrite the canvas.
  const handleBlogLiveUpdate = useCallback((data: BlogLiveData) => {
    setBlogLiveData((prev) => {
      if (!prev) return data  // First load — accept everything including publish state
      const blocks = blogBlocksFromPanelRef.current ? prev.blocks : data.blocks
      // Preserve publish state so split-button changes aren't overwritten by stale emitLive calls
      return { ...data, blocks, is_published: prev.is_published, published_at: prev.published_at }
    })
  }, [])

  // Called by CampaignsTab/CampaignEditor on every metadata or initial-load change
  const handleCampaignLiveUpdate = useCallback((data: CampaignLiveData) => {
    setCampaignLiveData((prev) => {
      if (!prev) return data
      // Preserve blocks that have been modified in the canvas
      return { ...data, blocks: prev.blocks }
    })
  }, [])

  // NOS-270: blog and campaign block edits share one shape — mutate the live
  // blocks, keep order_index sequential, debounce-save. Blog additionally
  // marks changes as panel-originated so stale emitLive calls don't clobber
  // them, and saves against the active post id (campaigns carry their own id).
  const blogBlocks = useMemo(
    () =>
      makeLiveBlockHandlers<BlogLiveData>(
        setBlogLiveData,
        (_prev, blocks) => {
          if (activeBlogPostId) void debouncedBlogBlockSave(activeBlogPostId, blocks)
        },
        () => {
          blogBlocksFromPanelRef.current = true
        }
      ),
    [activeBlogPostId, debouncedBlogBlockSave]
  )

  const campaignBlocks = useMemo(
    () =>
      makeLiveBlockHandlers<CampaignLiveData>(setCampaignLiveData, (prev, blocks) => {
        void debouncedCampaignBlockSave(prev.id, blocks)
      }),
    [debouncedCampaignBlockSave]
  )

  const makeBlogBlock = useCallback(
    (type: BlockType): PageSection => ({
      id: crypto.randomUUID(),
      section_type: type,
      order_index: 0,
      content: getDefaultBlockContent(type) as Record<string, unknown>,
      page_id: "",
      org_id: orgId ?? "",
      is_visible: true,
    }),
    [orgId]
  )

  // Called by "+ Add block" button in BlogPostEditor (inside ContentSwitcherPanel)
  const handleBlogBlockAdd = useCallback(
    (type: BlockType) => blogBlocks.append(makeBlogBlock(type)),
    [blogBlocks, makeBlogBlock]
  )

  // Ensure orgs are loaded when landing on builder (builder has no DashboardLayout, so fetchUserOrgs may not have run)
  useEffect(() => {
    if (user) {
      fetchUserOrgs()
    }
  }, [user, fetchUserOrgs])

  // Load customization store on mount
  useEffect(() => {
    if (orgId) {
      load(orgId)
    }
  }, [orgId, load])

  // NOS-116: Auto-show ImportReviewPanel when sections need review
  useEffect(() => {
    if (!importSectionsLoading && importSections.length > 0) {
      setShowImportReviewPanel(true)
    }
  }, [importSectionsLoading, importSections.length])

  // NOS-198: land on ?page=<slug> when opened from the import preview, once
  // (then strip the param so refresh/back doesn't re-trigger the jump).
  const pageParamAppliedRef = useRef(false)
  useEffect(() => {
    if (pageParamAppliedRef.current || pages.length === 0) return
    const params = new URLSearchParams(window.location.search)
    const wanted = params.get("page")
    if (!wanted) {
      pageParamAppliedRef.current = true
      return
    }
    const match = pages.find((p) => p.slug === wanted)
    if (match) {
      pageParamAppliedRef.current = true
      void setCurrentPage(match)
      params.delete("page")
      const qs = params.toString()
      window.history.replaceState(null, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`)
    }
  }, [pages, setCurrentPage])

  // Behavioral Rule #9: Escape key closes AI command bar
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setShowAIBar(false)
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [])

  // NOS-154: Cmd+Z / Shift+Cmd+Z (Ctrl on non-Mac) — page-scoped undo/redo
  useUndoRedoShortcuts(undo, redo)

  // Close viewport picker on outside click
  useEffect(() => {
    if (!showViewportPicker) return
    function handleClick(e: MouseEvent) {
      if (viewportPickerRef.current && !viewportPickerRef.current.contains(e.target as Node)) {
        setShowViewportPicker(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [showViewportPicker])

  // Called from BlogPostCanvas gap inserter — opens BlockPicker for blog blocks
  const handleBlogAddBlockAt = useCallback((afterIndex: number) => {
    setBlogPickerAfterIndex(afterIndex)
    setBlogBlockPickerOpen(true)
    setShowContentPanel(false)
  }, [])

  // Called when BlockPicker selects a type for a blog block
  const handleBlogPickerSelect = useCallback((type: BlockType) => {
    setBlogBlockPickerOpen(false)
    if (blogPickerAfterIndex === null) return
    const afterIdx = blogPickerAfterIndex
    setBlogPickerAfterIndex(null)
    blogBlocks.insertAt(makeBlogBlock(type), afterIdx)
  }, [blogPickerAfterIndex, blogBlocks, makeBlogBlock])

  // Inject Google Fonts
  useEffect(() => {
    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = GOOGLE_FONTS_URL
    document.head.appendChild(link)
    return () => {
      document.head.removeChild(link)
    }
  }, [])

  const handleExit = () => navigate("/dashboard")
  const handlePreview = () => {
    const slug = org?.slug
    if (!slug) return
    const base = window.location.origin
    const effectivePage = pageSlug ?? "home"
    const path = effectivePage === "home" ? `/${slug}` : `/${slug}/${effectivePage}`
    const url = `${base}${path}?preview=true`
    window.open(url, "_blank")
  }
  const handlePublish = async () => {
    try {
      await publishWebsite()
      toast.success("Page published")
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to publish"
      toast.error(msg)
    }
  }

  const handlePageConvertToDraft = async () => {
    try {
      await unpublishPage()
      toast.success("Converted to draft")
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to unpublish")
    }
  }

  const handleBlogPublish = async () => {
    if (!activeBlogPostId) return
    try {
      const publishedAt = new Date().toISOString()
      await updateBlogPost(activeBlogPostId, { is_published: true, published_at: publishedAt })
      setBlogLiveData((prev) => prev ? { ...prev, is_published: true, published_at: publishedAt } : prev)
      toast.success("Post published")
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to publish")
    }
  }

  const handleBlogConvertToDraft = async () => {
    if (!activeBlogPostId) return
    try {
      await updateBlogPost(activeBlogPostId, { is_published: false, published_at: null })
      setBlogLiveData((prev) => prev ? { ...prev, is_published: false, published_at: null } : prev)
      toast.success("Converted to draft")
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update post")
    }
  }

  const handleBlogSchedule = async (dateLocal: string) => {
    if (!activeBlogPostId || !dateLocal) return
    try {
      const publishedAt = new Date(dateLocal).toISOString()
      await updateBlogPost(activeBlogPostId, { is_published: true, published_at: publishedAt })
      setBlogLiveData((prev) => prev ? { ...prev, is_published: true, published_at: publishedAt } : prev)
      toast.success("Post scheduled")
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to schedule post")
    }
  }

  const handleBlogDelete = async () => {
    if (!activeBlogPostId) return
    if (!window.confirm("Delete this post? This cannot be undone.")) return
    try {
      await deleteBlogPost(activeBlogPostId)
      setActiveBlogPostId(null)
      setBlogLiveData(null)
      toast.success("Post deleted")
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete post")
    }
  }
  const handlePageSelect = (page: PageSummary) => {
    setCurrentPage(page)
    setActiveBlogPostId(null)
    setBlogLiveData(null)
    setActiveCampaignId(null)
    setCampaignLiveData(null)
  }

  const handleDeletePage = async (page: PageSummary, e: React.MouseEvent) => {
    e.stopPropagation()
    if (page.slug === "home") return
    if (!window.confirm(`Delete page "${page.title ?? page.slug ?? 'Untitled'}"? This cannot be undone.`)) return
    const ok = await deletePage(page.id)
    if (ok) {
      await removePage(page.id)
      toast.success("Page deleted")
    } else {
      toast.error("Failed to delete page")
    }
  }

  const handleAddPage = async (slug: string, title: string, addToNav: boolean) => {
    if (!orgId) return
    const normalizedSlug = slug.trim().toLowerCase().replace(/\s+/g, "-")
    const pageType = normalizedSlug === "about" ? "about" : "custom"
    const created = await createPage({
      org_id: orgId,
      slug: normalizedSlug,
      title: title?.trim() || null,
      page_type: pageType,
      ...(pageType === "about" ? { orgName: (org as Organization | null)?.name ?? undefined } : {}),
    })
    if (created) {
      addPage({ id: created.id, slug: created.slug, title: created.title, is_published: created.is_published })
      await setCurrentPage({ id: created.id, slug: created.slug, title: created.title, is_published: created.is_published })
      await addSection("footer", -1)
      if (addToNav) {
        const navLabel = title?.trim() || created.title || created.slug || "Page"
        await addNavItem({
          org_id: orgId,
          label: navLabel,
          href: `/${created.slug ?? normalizedSlug}`,
          order_index: navItems.length,
        })
        await refreshNav()
      }
      toast.success("Page created")
    } else {
      toast.error("Failed to create page")
    }
  }

  // Behavioral Rule #1: Clicking section selects it AND closes Brand/Content panels.
  // Cmd+click (Mac) toggles multi-select for merge. Multi-select skips opening context panel.
  const handleSectionClick = (sectionId: string, e?: React.MouseEvent) => {
    if (e?.metaKey || e?.ctrlKey) {
      // Prevent hero/footer from being multi-selected (they can't be merged/deleted)
      const section = sections.find(s => s.id === sectionId)
      if (section?.section_type === "hero" || section?.section_type === "footer") return
      setSelectedSectionIds(prev =>
        prev.includes(sectionId) ? prev.filter(id => id !== sectionId) : [...prev, sectionId]
      )
      setSelectedSectionId(null)
      return
    }
    setSelectedSectionIds([])
    setSelectedSectionId(sectionId)
    setShowBrandPanel(false)
    setShowContentPanel(false)
  }

  // Merge selected sections into a single image_text block
  const handleMergeSections = async () => {
    if (selectedSectionIds.length < 2) return
    const selected = sections.filter(s => selectedSectionIds.includes(s.id))
    const sorted = [...selected].sort((a, b) => a.order_index - b.order_index)

    // Collect heading and body from each selected section
    let heading = ""
    const bodyParts: string[] = []
    for (const s of sorted) {
      const c = (s.content as Record<string, unknown>) ?? {}
      if (s.section_type === "heading") {
        if (!heading) heading = (c.text as string) ?? ""
        else if (c.text) bodyParts.push(c.text as string)
      } else if (s.section_type === "paragraph") {
        if (c.text) bodyParts.push(c.text as string)
      } else if (s.section_type === "image_text") {
        if (!heading && c.heading) heading = c.heading as string
        if (c.body) bodyParts.push(c.body as string)
      } else {
        if (!heading && c.heading) heading = c.heading as string
        if (c.body) bodyParts.push(c.body as string)
        if (c.text) bodyParts.push(c.text as string)
      }
    }

    // Insert merged section before the first selected section
    const allSorted = [...sections].sort((a, b) => a.order_index - b.order_index)
    const firstIdx = allSorted.findIndex(s => s.id === sorted[0].id)
    const insertAfterIdx = Math.max(0, firstIdx - 1)

    try {
      const newSection = await addSection("image_text", insertAfterIdx)
      if (!newSection) { toast.error("Merge failed"); return }
      await updateSection(newSection.id, { heading, body: bodyParts.join("\n\n") })
      for (const s of sorted) await removeSection(s.id)
      setSelectedSectionIds([])
      setSelectedSectionId(newSection.id)
      toast.success(`Merged ${sorted.length} sections`)
    } catch {
      toast.error("Merge failed")
    }
  }

  // Close other panels when Add Section catalog opens; close catalog when another panel opens
  const handleCatalogOpen = () => {
    setSelectedSectionId(null)
    setShowBrandPanel(false)
    setShowContentPanel(false)
  }

  // Behavioral Rule #2: Clicking canvas background deselects section
  const handleCanvasBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setSelectedSectionId(null)
      setSelectedSectionIds([])
    }
  }

  // Reload customization store after import-review actions so current page sections update
  const handleImportReviewRefresh = useCallback(() => {
    refreshImportSections()
    if (pageId) load(orgId!, pageId)
  }, [refreshImportSections, pageId, load, orgId])

  // NOS-90/NOS-101: Add section and auto-select the new section. Surface errors via toast.
  const handleAddSection = async (sectionType: string, afterIndex: number) => {
    try {
      const newSection = await addSection(sectionType, afterIndex)
      if (newSection) {
        setSelectedSectionId(newSection.id)
        return newSection.id
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to add section"
      toast.error(msg)
    }
    return null
  }

  // NOS-92: Reorder sections on canvas drag (optimistic UI via store)
  const handleReorderSections = async (newOrder: PageSection[]) => {
    try {
      await reorderSections(newOrder)
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to reorder sections"
      toast.error(msg)
    }
  }

  const currentPageLabel = pageTitle ?? pageSlug ?? "Home"

  if (!orgId) {
    const noOrgStyles: React.CSSProperties = {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      gap: 20,
      color: "rgba(255,255,255,0.7)",
    }
    return (
      <div className="builder-root" style={builderRootStyles}>
        <div style={noOrgStyles}>
          {orgLoading ? (
            <span style={{ fontSize: 14 }}>Loading…</span>
          ) : userOrgs && userOrgs.length > 0 ? (
            <>
              <span style={{ fontSize: 14 }}>Select an organization to edit</span>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  minWidth: 240,
                }}
              >
                {userOrgs.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => switchOrg(o.id)}
                    style={{
                      padding: "12px 16px",
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: 8,
                      color: "#fff",
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    {o.name}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <span style={{ fontSize: 14 }}>
                You don&apos;t have an organization yet.
              </span>
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                style={{
                  padding: "10px 20px",
                  background: "#3b82f6",
                  border: "none",
                  borderRadius: 8,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Go to Dashboard
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="builder-root" style={builderRootStyles}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "rgba(239,68,68,0.9)",
          }}
        >
          {loadError}
        </div>
      </div>
    )
  }

  const subdomainUrl = org?.slug
    ? `${org.slug}.${window.location.hostname === "localhost" ? "localhost" : "getnos.ai"}`
    : "—"

  const viewportLabel = {
    desktop: "🖥 Desktop",
    tablet: "⬜ Tablet",
    mobile: "📲 Mobile",
  }[viewMode]

  const truncatedTitle = (() => {
    const raw = activeCampaignId
      ? (campaignLiveData?.title || "Campaign")
      : activeBlogPostId
        ? (blogLiveData?.title || "Blog post")
        : currentPageLabel
    return raw.length > 60 ? raw.slice(0, 60) + "…" : raw
  })()

  const publishStatus =
    activeCampaignId && campaignLiveData
      ? (campaignLiveData.is_published ? "published" : "draft")
      : activeBlogPostId && blogLiveData
        ? getStatus(blogLiveData.is_published, blogLiveData.published_at)
        : (isPublished ? "published" : "draft")

  return (
    <div className="builder-root" style={builderRootStyles}>
      <style>{builderCSS}</style>

      {/* Mini bar — org context, back button, NOS branding */}
      <div
        style={{
          height: 32,
          background: "rgba(8,8,12,0.98)",
          borderBottom: "1px solid rgba(255,255,255,0.04)",
          display: "flex",
          alignItems: "center",
          padding: "0 12px",
          gap: 10,
          flexShrink: 0,
          zIndex: 201,
        }}
      >
        <button
          type="button"
          onClick={handleExit}
          style={{
            width: 22,
            height: 22,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 5,
            color: "rgba(255,255,255,0.45)",
            fontSize: 12,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          ←
        </button>
        <span
          style={{
            fontSize: 13,
            fontWeight: 800,
            color: "rgba(255,255,255,0.9)",
            letterSpacing: "-0.04em",
            flexShrink: 0,
          }}
        >
          NOS
        </span>
        <div style={{ width: 1, height: 12, background: "rgba(255,255,255,0.08)", flexShrink: 0 }} />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <span
            data-testid="builder-subdomain-label"
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: "rgba(255,255,255,0.35)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {subdomainUrl}
          </span>
        </div>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: "2px 8px",
            borderRadius: 20,
            flexShrink: 0,
            ...(publishStatus === "published"
              ? { background: "rgba(34,197,94,0.15)", color: "#22c55e" }
              : publishStatus === "scheduled"
                ? { background: "rgba(245,158,11,0.15)", color: "#f59e0b" }
                : { background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }),
          }}
        >
          {publishStatus === "published" ? "Published" : publishStatus === "scheduled" ? "Scheduled" : "Draft"}
        </span>
      </div>

      {/* Main top bar */}
      <header
        className="top-bar"
        style={{
          height: 52,
          background: "rgba(12,12,16,0.97)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 16px",
          flexShrink: 0,
          zIndex: 200,
        }}
      >
        {/* Left — content switcher (page/blog title) */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
          <button
            type="button"
            data-testid="builder-content-switcher"
            onClick={() => {
              const next = !showContentPanel
              setShowContentPanel(next)
              if (next) {
                setShowBrandPanel(false)
                setSelectedSectionId(null)
              }
            }}
            style={{
              background: showContentPanel ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 7,
              padding: "6px 12px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#fff",
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
              minWidth: 0,
              maxWidth: 400,
            }}
          >
            <span style={{ flexShrink: 0 }}>
              {activeCampaignId ? "🎯" : activeBlogPostId ? "📝" : "📄"}
            </span>
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {truncatedTitle}
            </span>
            <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", flexShrink: 0 }}>▼</span>
          </button>
        </div>

        {/* Center — condensed viewport picker */}
        <div style={{ position: "relative", flexShrink: 0 }} ref={viewportPickerRef}>
          <button
            type="button"
            onClick={() => setShowViewportPicker((v) => !v)}
            style={{
              padding: "5px 12px",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 7,
              fontSize: 11,
              fontWeight: 500,
              cursor: "pointer",
              background: showViewportPicker ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.06)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              gap: 6,
              transition: "all 0.12s",
            }}
          >
            <span>{viewportLabel}</span>
            <span style={{ fontSize: 8, color: "rgba(255,255,255,0.4)" }}>▼</span>
          </button>
          {showViewportPicker && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(16,16,22,0.98)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                border: "1px solid rgba(255,255,255,0.09)",
                borderRadius: 10,
                boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
                zIndex: 300,
                overflow: "hidden",
                minWidth: 130,
              }}
            >
              {(["desktop", "tablet", "mobile"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => {
                    setViewMode(mode)
                    setShowViewportPicker(false)
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 14px",
                    background: viewMode === mode ? "rgba(255,255,255,0.08)" : "transparent",
                    border: "none",
                    color: viewMode === mode ? "#fff" : "rgba(255,255,255,0.5)",
                    fontSize: 12,
                    fontWeight: viewMode === mode ? 600 : 400,
                    cursor: "pointer",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    transition: "background 0.1s",
                  }}
                >
                  <span>
                    {mode === "desktop" ? "🖥" : mode === "tablet" ? "⬜" : "📲"}
                  </span>
                  <span style={{ textTransform: "capitalize" }}>{mode}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right section */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, justifyContent: "flex-end" }}>
          {/* Page-level undo/redo - NOS-154 */}
          <button
            type="button"
            title={undoStack.length > 0 ? `Undo last change (${undoStack.length} available)` : "Nothing to undo"}
            disabled={undoStack.length === 0}
            onClick={() => void undo()}
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.06)",
              color: undoStack.length > 0 ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.25)",
              cursor: undoStack.length > 0 ? "pointer" : "default",
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            <span style={{ fontSize: 13 }}>↩</span>
            Undo
          </button>
          <button
            type="button"
            title={redoStack.length > 0 ? `Redo (${redoStack.length} available)` : "Nothing to redo"}
            disabled={redoStack.length === 0}
            onClick={() => void redo()}
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.06)",
              color: redoStack.length > 0 ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.25)",
              cursor: redoStack.length > 0 ? "pointer" : "default",
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            <span style={{ fontSize: 13 }}>↪</span>
            Redo
          </button>

          {/* Outline / Visual mode toggle */}
          <button
            type="button"
            data-testid="builder-outline-toggle"
            title={outlineMode ? "Switch to visual editor" : "Switch to outline view"}
            onClick={() => {
              setOutlineMode(v => !v)
              setSelectedSectionId(null)
              setSelectedSectionIds([])
            }}
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: outlineMode ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.06)",
              color: outlineMode ? "#fff" : "rgba(255,255,255,0.5)",
              cursor: "pointer",
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            {outlineMode ? (
              <>
                <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
                  <circle cx="8" cy="8" r="2.5"/>
                  <path d="M8 2C4.5 2 1.5 5 1.5 8S4.5 14 8 14 14.5 11 14.5 8 11.5 2 8 2zm0 10c-2.8 0-5-1.8-5.4-4C3 5.8 5.2 4 8 4s5 1.8 5.4 4C13 10.2 10.8 12 8 12z"/>
                </svg>
                Visual
              </>
            ) : (
              <>
                <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
                  <rect x="1" y="2" width="14" height="2" rx="1"/>
                  <rect x="1" y="7" width="10" height="2" rx="1"/>
                  <rect x="1" y="12" width="12" height="2" rx="1"/>
                </svg>
                Outline
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              const next = !showBrandPanel
              setShowBrandPanel(next)
              if (next) {
                setSelectedSectionId(null)
                setShowContentPanel(false)
                setShowPageSettings(false)
              }
            }}
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: showBrandPanel ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.06)",
              color: showBrandPanel ? "#fff" : "rgba(255,255,255,0.5)",
              cursor: "pointer",
            }}
          >
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                background: primaryColor,
              }}
            />
            <span style={{ fontSize: 11, fontWeight: 500 }}>Brand</span>
          </button>
          {importSections.length > 0 && (
            <button
              type="button"
              onClick={() => setShowImportReviewPanel(true)}
              style={{
                padding: "6px 12px",
                background: showImportReviewPanel ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.08)",
                border: "1px solid rgba(245,158,11,0.3)",
                borderRadius: 8,
                color: "#f59e0b",
                fontSize: 11,
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 700, background: "rgba(245,158,11,0.2)", borderRadius: 4, padding: "1px 5px" }}>
                {importSections.length}
              </span>
              Review
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowAIBar(!showAIBar)}
            style={{
              padding: "6px 12px",
              background: `linear-gradient(135deg, ${primaryColor}25, ${primaryColor}08)`,
              border: `1px solid ${primaryColor}35`,
              borderRadius: 8,
              color: primaryColor,
              fontSize: 11,
              fontWeight: 500,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            <span aria-hidden style={{ fontSize: 12, lineHeight: 1 }}>
              ✦
            </span>
            <span>AI</span>
          </button>
          <button
            type="button"
            data-testid="builder-preview-button"
            onClick={handlePreview}
            style={{
              padding: "6px 16px",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 8,
              color: "rgba(255,255,255,0.6)",
              fontSize: 11,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Preview
          </button>
          {/* Split publish button (campaigns use Publish toggle inside CampaignCanvas) */}
          {activeCampaignId ? (
            <span
              style={{
                padding: "6px 12px",
                fontSize: 11,
                color: "rgba(255,255,255,0.45)",
                whiteSpace: "nowrap",
              }}
            >
              Use Publish in campaign editor
            </span>
          ) : (
          <PublishMenu
            primaryColor={primaryColor}
            blog={
              activeBlogPostId
                ? {
                    status: publishStatus,
                    isPublished: Boolean(blogLiveData?.is_published),
                    onPublish: () => void handleBlogPublish(),
                    onSchedule: (dateLocal) => void handleBlogSchedule(dateLocal),
                    onConvertToDraft: () => void handleBlogConvertToDraft(),
                    onDelete: () => void handleBlogDelete(),
                  }
                : null
            }
            page={{
              isPublished,
              onPublish: () => void handlePublish(),
              onOpenSettings: () => {
                setShowBrandPanel(false)
                setShowPageSettings(true)
              },
              onConvertToDraft: () => void handlePageConvertToDraft(),
              onDelete:
                pageSlug !== "home"
                  ? (e) => {
                      const page = pages.find((p) => p.id === pageId)
                      if (page) void handleDeletePage(page, e)
                    }
                  : null,
            }}
          />
          )}
        </div>
      </header>

      {/* NOS-160: AI setup progress / retry (replaces the old silent failure path) */}
      {orgId && (
        <GenerationStatusBar
          orgId={orgId}
          onGenerated={() => {
            if (orgId) void load(orgId, pageSlug ?? undefined)
          }}
        />
      )}

      {/* Canvas area - Section 4. Spec: flex, justify-content center, padding 24px 52px */}
      <div
        className="canvas-area"
        role="presentation"
        onClick={handleCanvasBackgroundClick}
        style={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <div
          className={`canvas-wrap ${viewMode}`}
          style={{
            width: "100%",
            maxWidth: viewMode === "desktop" ? 960 : viewMode === "tablet" ? 768 : 375,
            transition: "max-width 0.3s ease",
            alignSelf: "flex-start",
          }}
        >
          <div
            className="preview-frame"
            style={{
              borderRadius: 10,
              clipPath: "inset(0 round 10px)",
              boxShadow:
                "0 0 0 1px rgba(255,255,255,0.05), 0 24px 60px rgba(0,0,0,0.5)",
              background: "#fff",
              minHeight: 400,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {outlineMode ? (
              <OutlineView
                sections={sections}
                primaryColor={primaryColor}
              />
            ) : activeCampaignId && campaignLiveData ? (
              <CampaignCanvas
                liveData={campaignLiveData}
                customizations={customizations ?? null}
                org={org as Organization | null}
                navItems={navItems}
                pages={pages}
                selectedSectionId={selectedSectionId}
                onSectionClick={handleSectionClick}
                onReorderBlocks={campaignBlocks.reorder}
                footerSection={sections.find((s) => s.section_type === "footer") ?? null}
                isMobileViewport={viewMode === "mobile"}
                primaryColor={primaryColor}
              />
            ) : activeCampaignId && !campaignLiveData ? (
              <div
                style={{
                  padding: 48,
                  color: "#999",
                  fontSize: 14,
                  textAlign: "center",
                  background: "#fff",
                }}
              >
                Loading campaign…
              </div>
            ) : activeBlogPostId && blogLiveData ? (
              <BlogPostCanvas
                liveData={blogLiveData}
                customizations={customizations ?? null}
                org={org as Organization | null}
                navItems={navItems}
                pages={pages}
                selectedSectionId={selectedSectionId}
                onSectionClick={handleSectionClick}
                onAddBlockAt={handleBlogAddBlockAt}
                onReorderBlocks={blogBlocks.reorder}
                footerSection={sections.find((s) => s.section_type === "footer") ?? null}
                isMobileViewport={viewMode === "mobile"}
              />
            ) : (
              <InlinePreview
                org={org as Organization | null}
                customizations={customizations ?? null}
                sections={sections}
                pages={pages}
                navItems={navItems}
                selectedSectionId={selectedSectionId}
                selectedSectionIds={selectedSectionIds}
                onSectionClick={handleSectionClick}
                onAddSection={handleAddSection}
                onSelectSection={setSelectedSectionId}
                onReorderSections={handleReorderSections}
                onCatalogOpen={handleCatalogOpen}
                catalogShouldClose={selectedSectionId !== null || showBrandPanel}
                isMobileViewport={viewMode === "mobile"}
                isHomePage={pageSlug === "home"}
              />
            )}
          </div>
        </div>
      </div>

      {/* Brand panel - NOS-99. One panel at a time. */}
      {showBrandPanel && (
        <BrandPanel
          primaryColor={primaryColor}
          onClose={() => setShowBrandPanel(false)}
        />
      )}

      {/* Nav context panel - NOS-99. Shown when nav bar is clicked. */}
      {selectedSectionId === "__nav__" && (
        <NavContextPanel
          orgId={orgId}
          pages={pages}
          navItems={navItems}
          navCtaText={customizations?.nav_cta_text ?? "Donate"}
          onUpdateNavCtaText={(text) => void updateNavCtaText(text)}
          primaryColor={primaryColor}
          onRefresh={refreshNav}
          onClose={() => setSelectedSectionId(null)}
          onOpenPageSettings={() => {
            setSelectedSectionId(null)
            setShowBrandPanel(false)
            setShowPageSettings(true)
          }}
          onOpenAnnouncement={() => setSelectedSectionId("__announcement__")}
        />
      )}

      {/* NOS-239: announcement bar panel — opened from the nav panel or by
          clicking the bar on the canvas (synthetic id "__announcement__"). */}
      {selectedSectionId === "__announcement__" && (
        <AnnouncementPanel
          announcement={customizations?.announcement}
          primaryColor={primaryColor}
          onUpdate={(config) => void updateAnnouncement(config)}
          onClose={() => setSelectedSectionId(null)}
        />
      )}

      {/* Page settings panel. Left side, same position as Brand panel. */}
      {showPageSettings && !activeBlogPostId && !activeCampaignId && (
        <div
          className="animate-slideIn"
          data-testid="page-settings-panel"
          style={{
            ...PANEL_CHROME,
            top: 100,
            left: 16,
            bottom: 16,
            width: 272,
            zIndex: 1000,
          }}
        >
          <div
            style={{
              padding: "14px 16px",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: primaryColor }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>
                Page settings
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowPageSettings(false)}
              style={{
                width: 26,
                height: 26,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 7,
                cursor: "pointer",
                fontSize: 11,
                color: "rgba(255,255,255,0.5)",
              }}
            >
              ✕
            </button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
            <PageSettingsPanel
              orgId={orgId}
              pageId={pageId}
              onSettingsChange={() => {
                if (orgId && pageId) void refreshNav()
              }}
            />
          </div>
        </div>
      )}

      {/* AI Command Bar - NOS-96, Section 14. Rule #9: Escape closes (handled in useEffect). */}
      {showAIBar && (
        <AICommandBar
          primaryColor={primaryColor}
          sections={sections}
          onAddSection={handleAddSection}
          onClose={() => setShowAIBar(false)}
        />
      )}

      {/* Section context panel - NOS-5/7. Show when section selected and not nav. Behavioral Rule #2: canvas click closes. */}
      {selectedSectionId &&
        selectedSectionId !== "__nav__" &&
        selectedSectionId !== "__announcement__" &&
        (() => {
          const campaignBlock = activeCampaignId && campaignLiveData
            ? campaignLiveData.blocks.find((b) => b.id === selectedSectionId)
            : undefined
          const blogBlock = !campaignBlock && activeBlogPostId && blogLiveData
            ? blogLiveData.blocks.find((b) => b.id === selectedSectionId)
            : undefined
          const section = campaignBlock ?? blogBlock ?? sections.find((s) => s.id === selectedSectionId)
          return section ? (
            <SectionContextPanel
              section={section}
              primaryColor={primaryColor}
              onClose={() => setSelectedSectionId(null)}
              onUpdateSection={
                campaignBlock ? campaignBlocks.update
                  : blogBlock ? blogBlocks.update
                  : undefined
              }
              onRemoveSection={
                campaignBlock ? campaignBlocks.remove
                  : blogBlock ? blogBlocks.remove
                  : undefined
              }
              width={(activeCampaignId || activeBlogPostId) ? Math.floor(window.innerWidth / 2) : undefined}
              titleOverride={activeBlogPostId && section.section_type === "heading" ? "Content" : undefined}
              isHomePage={pageSlug === "home"}
            />
          ) : null
        })()}

      {/* Multi-select merge bar — shown when 2+ sections are Cmd+click selected */}
      {selectedSectionIds.length >= 2 && (
        <div
          style={{
            position: "fixed",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 200,
            background: "rgba(12,12,16,0.97)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(245,158,11,0.35)",
            borderRadius: 12,
            boxShadow: "0 12px 40px rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 16px",
          }}
        >
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", whiteSpace: "nowrap" }}>
            {selectedSectionIds.length} sections selected
          </span>
          <button
            type="button"
            onClick={() => void handleMergeSections()}
            style={{
              padding: "7px 16px",
              background: "rgba(245,158,11,0.2)",
              border: "1px solid rgba(245,158,11,0.5)",
              borderRadius: 8,
              color: "#f59e0b",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Merge into one section
          </button>
          <button
            type="button"
            onClick={() => setSelectedSectionIds([])}
            style={{
              background: "none",
              border: "none",
              color: "rgba(255,255,255,0.4)",
              fontSize: 18,
              cursor: "pointer",
              lineHeight: 1,
              padding: "0 2px",
            }}
            aria-label="Clear selection"
          >
            ✕
          </button>
        </div>
      )}

      {/* NOS-116/NOS-178/NOS-192: Import review panel — sections needing review,
          live job progress, and the preview gate while a job waits at review */}
      {showImportReviewPanel &&
        (importSections.length > 0 ||
          importJobActive ||
          importAwaitingReview ||
          // NOS-253: late entry — a done job with protected drafts keeps the
          // panel reachable so the recovery card can take the password
          importProtectedPages.length > 0) && (
        <ImportReviewPanel
          sections={importSections}
          isLoading={importSectionsLoading}
          onRefresh={handleImportReviewRefresh}
          onClose={() => setShowImportReviewPanel(false)}
          shiftLeft={
            selectedSectionId != null &&
            selectedSectionId !== "__nav__" &&
            selectedSectionId !== "__announcement__"
          }
          activeJob={importJobActive ? activeImportJob : null}
          imageFailures={importImageFailures}
          skippedPages={importSkippedPages}
          contentSummary={importContentSummary}
          overflowSummary={importOverflowSummary}
          importDesign={activeImportJob?.import_design ?? null}
          reviewJob={importAwaitingReview ? activeImportJob : null}
          remainingSummary={importRemainingSummary}
          onContinueImport={handleContinueImport}
          onStopImport={handleStopImport}
          report={importReport}
          brandProposal={brandProposal}
          isPlatformAdmin={isPlatformAdmin}
          decisions={importDecisions}
          decisionsJobId={activeImportJob?.id ?? null}
          orgId={orgId}
          importedPostSlugs={importedPostSlugs}
          retryJobId={activeImportJob?.id ?? null}
          onImageRetryComplete={() => {
            // NOS-235: retry rewrote content + failure list — refresh both
            refreshImportJob()
            refreshImportSections()
          }}
          screenshots={importScreenshots}
          onOpenCompare={(slug) => setCompareSlug(slug)}
          protectedPages={importProtectedPages}
          protectedRecovery={importProtectedRecovery}
          onProtectedRecovered={() => {
            // NOS-253: stubs were replaced with real content — refresh both
            refreshImportJob()
            refreshImportSections()
          }}
        />
      )}

      {/* NOS-183: side-by-side import review — source screenshot vs render */}
      {compareSlug != null && orgId && (
        <ImportCompareView
          sections={importSections}
          screenshots={importScreenshots}
          orgId={orgId}
          jobId={activeImportJob?.id ?? null}
          initialSlug={compareSlug}
          onClose={() => setCompareSlug(null)}
          onRefresh={refreshImportSections}
        />
      )}

      {/* Blog block picker — opened from canvas gap inserter */}
      <BlockPicker
        open={blogBlockPickerOpen}
        onClose={() => { setBlogBlockPickerOpen(false); setBlogPickerAfterIndex(null) }}
        onSelect={handleBlogPickerSelect}
      />

      {/* Content switcher panel */}
      {showContentPanel && orgId && (
        <ContentSwitcherPanel
          pages={pages}
          pageId={pageId}
          pageSlug={pageSlug}
          onSelectPage={handlePageSelect}
          onDeletePage={handleDeletePage}
          onAddPage={handleAddPage}
          pageCreating={pageCreating}
          orgId={orgId}
          activeBlogPostId={activeBlogPostId}
          onSelectBlogPost={(id) => {
            setActiveBlogPostId(id)
            if (!id) setBlogLiveData(null)
          }}
          onBlogLiveUpdate={handleBlogLiveUpdate}
          onBlogBlockAdd={handleBlogBlockAdd}
          activeCampaignId={activeCampaignId}
          onSelectCampaign={(id) => {
            setActiveCampaignId(id)
            setCampaignLiveData(null)
            if (id) setSelectedSectionId(null)
          }}
          onCampaignLiveUpdate={handleCampaignLiveUpdate}
          onCampaignBlockAdd={undefined}
          primaryColor={primaryColor}
          onClose={() => setShowContentPanel(false)}
          initialTab={initialContentTab}
        />
      )}
    </div>
  )
}

const builderRootStyles: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  background: "#08080c",
  overflow: "hidden",
  fontFamily: BUILDER_FONT,
}

const builderCSS = `
  .canvas-area {
    flex: 1;
    overflow-y: auto;
    display: flex;
    justify-content: center;
    padding: 24px 52px;
  }

  @media (max-width: 767px) {
    .canvas-area {
      padding: 16px 12px;
    }
  }

  @media (max-width: 419px) {
    .canvas-area {
      padding: 12px 8px;
    }
  }

  .canvas-area::-webkit-scrollbar {
    width: 5px;
  }
  .canvas-area::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3px;
  }
  .canvas-area::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.08);
    border-radius: 3px;
  }

  .canvas-wrap {
    width: 100%;
    max-width: 960px;
    transition: max-width 0.3s ease;
    align-self: flex-start;
  }
  .canvas-wrap.tablet {
    max-width: 768px;
  }
  .canvas-wrap.mobile {
    max-width: 375px;
  }

  @keyframes slideIn {
    from { opacity: 0; transform: translateX(10px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translate(-50%, 12px); }
    to { opacity: 1; transform: translate(-50%, 0); }
  }
  .animate-slideIn {
    animation: slideIn 0.18s ease-out;
  }
  .animate-slideUp {
    animation: slideUp 0.2s ease-out;
  }

  .page-item:hover {
    background: rgba(255,255,255,0.06) !important;
    color: #fff !important;
  }
  .page-item:hover .page-delete-btn {
    opacity: 1 !important;
  }

  .drag-zone:hover .drag-h {
    opacity: 1 !important;
  }
  .section-gap:hover .gap-btn {
    opacity: 1;
  }
`
